import { useEffect, useState, useMemo, useRef } from 'react'
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import { getObjectsWithFilter } from '../../api/apiClientCrud'

const SelectField = ({
  data = {},
  mode = 'edit',
  field = {},
  isError = false,
  onValueChange = () => {},
  isTable = false
}) => {
  const [select, setSelect] = useState([])
  const [loading, setLoading] = useState(false)

  const { name, type, defaultValue, fullWidth, required, readOnly, readOnlyWhen, depends, label, options = {} } = field

  const {
    objectAPI,
    object,
    filter: filterFn,
    subFilter: subFilterFn,
    type: optionsType,
    data: optionsData,
    returnField,
    value
  } = options

  const shouldBeReadOnly = mode === 'view' || readOnly || (readOnlyWhen && readOnlyWhen(data))
  const dependsValue = depends ? data[depends] : null

  const memoizedFilter = useMemo(() => {
    if (!filterFn) return null
    const cleanedData = Object.fromEntries(Object.entries(data).filter(([_, v]) => v !== ''))
    try {
      return JSON.stringify(filterFn(cleanedData))
    } catch {
      return null
    }
  }, [data, filterFn])

  const memoizedSubFilter = useMemo(() => {
    if (!subFilterFn) return null
    const cleanedData = Object.fromEntries(Object.entries(data).filter(([_, v]) => v !== ''))
    try {
      return JSON.stringify(subFilterFn(cleanedData))
    } catch {
      return null
    }
  }, [data, subFilterFn])

  const prevFilterRef = useRef('')
  const prevSubFilterRef = useRef('')

  const fetchSelect = async () => {
    setLoading(true)
    try {
      const parsedFilter = memoizedFilter ? JSON.parse(memoizedFilter) : null
      const parsedSubFilter = memoizedSubFilter ? JSON.parse(memoizedSubFilter) : null

      const res = await getObjectsWithFilter(objectAPI ?? object, parsedFilter, parsedSubFilter)
      setSelect(res.data[object] ?? res.data)
    } catch (error) {
      setSelect([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (type !== 'select' || !options) return
    if (optionsType !== 'api') {
      if (optionsType === 'data') {
        setSelect(optionsData || [])
      }
      return
    }

    if (
      prevFilterRef.current === memoizedFilter &&
      prevSubFilterRef.current === memoizedSubFilter &&
      dependsValue !== null
    ) {
      return
    }

    prevFilterRef.current = memoizedFilter
    prevSubFilterRef.current = memoizedSubFilter

    fetchSelect()
  }, [dependsValue, memoizedFilter, memoizedSubFilter, type, optionsType, optionsData])

  const defaultSetRef = useRef(false)

  useEffect(() => {
    if (defaultSetRef.current) return

    const valueFromData = data[name]
    const defVal = defaultValue ? defaultValue() : ''

    if ((valueFromData === undefined || valueFromData === null || valueFromData === '') && defVal !== '') {
      onValueChange({ target: { name, value: defVal } })
      defaultSetRef.current = true
    }
  }, [defaultValue, data[name], name, onValueChange])

  const rawValue = data[name] ?? (defaultValue ? defaultValue() : '')
  const valueFromData = rawValue === null ? '' : rawValue
  return (
    <FormControl
      variant={shouldBeReadOnly || isTable ? 'standard' : 'outlined'}
      fullWidth={fullWidth ?? false}
      required={required ?? false}
      sx={{ my: '5px', minWidth: '120px' }}
    >
      {!isTable && (
        <InputLabel id={`${name}-label`} error={isError}>
          {label}
        </InputLabel>
      )}
      <Select
        required={required ?? false}
        labelId={`${name}-label`}
        id={`${name}-select`}
        label={!isTable ? label : null}
        error={isError}
        value={valueFromData}
        name={name}
        inputProps={{ readOnly: shouldBeReadOnly }}
        onChange={onValueChange}
        disabled={loading || shouldBeReadOnly}
      >
        <MenuItem key={`${name}-empty`} value=''>
          <em>Tous</em>
        </MenuItem>
        {loading && (
          <MenuItem disabled key='loading'>
            <em>Chargement...</em>
          </MenuItem>
        )}
        {!loading &&
          optionsType !== 'custom' &&
          select?.map((item) => (
            <MenuItem key={`${name}-${item.id}`} value={item[returnField]}>
              {item[value]}
            </MenuItem>
          ))}
        {!loading && optionsType === 'custom' && options.component?.(data)}
      </Select>
    </FormControl>
  )
}

export default SelectField
